<template>
  <div class="members-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="members-shell">
      <!-- Stats -->
      <section class="dash-section fade-in delay-100">
        <div class="stats-grid">
          <div
            v-for="(stat, index) in stats"
            :key="stat.title"
            class="stat-card"
            :style="{ animationDelay: 0.05 * index + 's' }"
          >
            <div class="stat-top">
              <div class="stat-icon">
                <v-icon size="18" color="#F2F2ED">{{ stat.icon }}</v-icon>
              </div>
            </div>
            <div class="stat-value">{{ stat.value }}</div>
            <div class="stat-label">{{ stat.title }}</div>
          </div>
        </div>
      </section>

      <!-- Filtros -->
      <section class="dash-section fade-in delay-200">
        <div class="card-flat">
          <div class="filters-row">
            <div class="search-section">
              <div class="search-input-wrapper">
                <v-icon size="18" color="#5C5C62">mdi-magnify</v-icon>
                <input
                  v-model="busqueda"
                  type="text"
                  class="search-input"
                  placeholder="Buscar por nombre o matrícula"
                  @input="filtrarMiembros"
                />
                <button
                  v-if="busqueda"
                  class="clear-btn"
                  @click="busqueda = ''"
                >
                  <v-icon size="14" color="#5C5C62">mdi-close</v-icon>
                </button>
              </div>
            </div>

            <div class="action-buttons-row">
              <button class="btn-ghost" @click="exportarMiembros">
                <v-icon size="15" class="mr-2">mdi-export</v-icon>
                Exportar
              </button>
              <button
                v-if="puedeVer('miembros.importar')"
                class="btn-ghost"
                @click="$router.push('/miembros/importar')"
              >
                <v-icon size="15" class="mr-2">mdi-file-upload-outline</v-icon>
                Importar
              </button>
              <button
                v-if="puedeVer('miembros.crear')"
                class="btn-primary"
                @click="$router.push('/nuevo-miembro')"
              >
                <v-icon size="15" class="mr-2">mdi-account-plus</v-icon>
                Nuevo miembro
              </button>
            </div>
          </div>

          <div class="section-divider"></div>

          <div class="quick-filters">
            <button
              v-for="filter in quickFilters"
              :key="filter.label"
              :class="['filter-chip', { 'filter-chip-active': filter.active }]"
              @click="aplicarFiltro(filter.value)"
            >
              <v-icon size="14" class="mr-1">{{ filter.icon }}</v-icon>
              {{ filter.label }}
            </button>
          </div>
        </div>
      </section>

      <!-- Grid de miembros -->
      <section class="dash-section fade-in delay-300">
        <div v-if="cargando" class="loading-state">
          <div class="spinner"></div>
          <p class="loading-text">Cargando miembros…</p>
        </div>

        <div v-else-if="miembrosFiltrados.length === 0" class="empty-state">
          <div class="empty-icon">
            <v-icon size="30" color="#5C5C62">mdi-account-search</v-icon>
          </div>
          <h3 class="empty-title">No se encontraron miembros</h3>
          <p class="empty-desc">
            No hay miembros que coincidan con tu búsqueda
          </p>
          <button class="btn-primary mt-lg" @click="reiniciarFiltros">
            <v-icon size="15" class="mr-2">mdi-refresh</v-icon>
            Mostrar todos
          </button>
        </div>

        <div v-else class="members-grid">
          <div
            v-for="(miembro, index) in miembrosPaginados"
            :key="miembro.id"
            class="member-card"
            :class="estadoHeaderClass(miembro.estado)"
            :style="{ animationDelay: 0.03 * index + 's' }"
          >
            <!-- Header -->
            <div class="member-card-top">
              <div class="member-avatar-wrapper">
                <img
                  :src="urlImagen(miembro.imagen)"
                  alt="Avatar"
                  class="member-avatar"
                  @error="$event.target.src = 'https://via.placeholder.com/80'"
                />
              </div>
              <span :class="['status-tag', estadoBadgeClass(miembro.estado)]">
                <v-icon size="11" class="mr-1">{{
                  estadoIcon(miembro.estado)
                }}</v-icon>
                {{ miembro.estado || "SIN MEMBRESÍA" }}
              </span>
            </div>

            <!-- Contenido -->
            <div class="member-card-content">
              <h3 class="member-name">{{ miembro.nombre }}</h3>

              <div class="member-details">
                <div class="detail-item">
                  <v-icon size="13" class="mr-1" color="#5C5C62"
                    >mdi-identifier</v-icon
                  >
                  <span class="detail-label">Matrícula</span>
                  <strong class="detail-value">{{ miembro.matricula }}</strong>
                </div>
                <div class="detail-item">
                  <v-icon size="13" class="mr-1" color="#5C5C62"
                    >mdi-phone</v-icon
                  >
                  <span class="detail-label">Teléfono</span>
                  <span class="detail-value-plain">{{
                    miembro.telefono || "No registrado"
                  }}</span>
                </div>
              </div>

              <div v-if="miembro.membresia" class="membership-block">
                <span
                  :class="[
                    'membership-chip',
                    claseMembresia(miembro.membresia)
                  ]"
                >
                  <v-icon size="13" class="mr-1">{{
                    iconoMembresia(miembro.membresia)
                  }}</v-icon>
                  {{ miembro.membresia }}
                </span>
                <div class="membership-dates">
                  <span class="date-mini">
                    <v-icon size="12" class="mr-1" color="#5C5C62"
                      >mdi-calendar-start</v-icon
                    >
                    {{ formatearFechaHora(miembro.fechaInicio) }}
                  </span>
                  <span class="date-mini">
                    <v-icon size="12" class="mr-1" color="#5C5C62"
                      >mdi-calendar-end</v-icon
                    >
                    {{ formatearFechaHora(miembro.fechaFin) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Acciones -->
            <div class="member-card-actions">
              <button
                class="icon-btn"
                @click="toggleExpanded(miembro)"
                title="Ver detalles"
              >
                <v-icon size="15" color="#8C8C93"
                  >mdi-information-outline</v-icon
                >
              </button>
              <button
                class="icon-btn"
                @click="verEstadoCuenta(miembro)"
                title="Estado de cuenta"
              >
                <v-icon size="15" color="#8C8C93">mdi-file-document</v-icon>
              </button>
              <button
                class="icon-btn"
                @click="generarCredencial(miembro)"
                title="Credencial"
              >
                <v-icon size="15" color="#8C8C93"
                  >mdi-card-account-details</v-icon
                >
              </button>
              <button
                v-if="puedeVer('miembros.editar')"
                class="icon-btn"
                @click="editar(miembro.id)"
                title="Editar"
              >
                <v-icon size="15" color="#8C8C93">mdi-pencil</v-icon>
              </button>
              <button
                v-if="
                  puedeVer('miembros.renovar') &&
                    (!miembro.estado || miembro.estado === 'VENCIDO')
                "
                class="icon-btn"
                @click="realizarPago(miembro)"
                title="Renovar"
              >
                <v-icon size="15" color="#C8FF4D">mdi-cash</v-icon>
              </button>
              <button
                v-if="puedeVer('miembros.eliminar')"
                class="icon-btn icon-btn-danger"
                @click="eliminar(miembro)"
                title="Eliminar"
              >
                <v-icon size="15" color="#FF6B6B">mdi-delete</v-icon>
              </button>
              <button
                v-if="
                  puedeVer('miembros.quitar_membresia') && miembro.idMembresia
                "
                class="icon-btn"
                @click="confirmarQuitarMembresia(miembro)"
                title="Quitar membresía (corrección de error)"
              >
                <v-icon size="15" color="#FFB84D">mdi-card-off-outline</v-icon>
              </button>
            </div>

            <!-- Contenido expandido -->
            <div v-if="expandedMember === miembro.id" class="member-expanded">
              <div class="section-divider"></div>
              <div class="expanded-grid">
                <div
                  v-for="(info, key) in additionalInfo(miembro)"
                  :key="key"
                  class="expanded-item"
                >
                  <v-icon size="14" class="mr-2" color="#5C5C62">{{
                    info.icon
                  }}</v-icon>
                  <div>
                    <div class="expanded-label">{{ info.label }}</div>
                    <div class="expanded-value">{{ info.value }}</div>
                  </div>
                </div>
              </div>

              <div v-if="miembro.nombreContacto" class="alert-box mt-md">
                <v-icon size="18" color="#FFB84D">mdi-alert-circle</v-icon>
                <div>
                  <div class="alert-title">Contacto de emergencia</div>
                  <div class="alert-text">
                    {{ miembro.nombreContacto }} — Tel:
                    {{ miembro.telefonoContacto }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="miembrosFiltrados.length > 0 && !cargando"
          class="pagination-bar"
        >
          <span class="pagination-info">
            Mostrando {{ rangoInicio }}–{{ rangoFin }} de
            {{ miembrosFiltrados.length }}
          </span>

          <div class="pagination-controls">
            <button
              class="page-btn"
              :disabled="paginaActual === 1"
              @click="paginaActual--"
            >
              <v-icon size="16">mdi-chevron-left</v-icon>
            </button>

            <button
              v-for="pagina in paginasVisibles"
              :key="pagina"
              class="page-btn"
              :class="{ 'page-btn-active': pagina === paginaActual }"
              @click="paginaActual = pagina"
            >
              {{ pagina }}
            </button>

            <button
              class="page-btn"
              :disabled="paginaActual === totalPaginas"
              @click="paginaActual++"
            >
              <v-icon size="16">mdi-chevron-right</v-icon>
            </button>
          </div>

          <select v-model.number="porPagina" class="per-page-select">
            <option :value="24">24 por página</option>
            <option :value="48">48 por página</option>
            <option :value="96">96 por página</option>
          </select>
        </div>
      </section>
    </div>

    <!-- FAB -->
    <button
      v-if="puedeVer('miembros.crear')"
      class="fab-button"
      @click="$router.push('/nuevo-miembro')"
    >
      <v-icon size="22" color="#0E0E10">mdi-plus</v-icon>
    </button>

    <!-- Dialog: Realizar Pago -->
    <v-dialog v-model="mostrarRealizarPago" persistent max-width="600">
      <realizar-pago
        :matricula="matriculaSeleccionada"
        :miembro="miembroSeleccionadoPago"
        @cerrar="cerrarDialogoPago"
        @pagado="onPagado"
      />
    </v-dialog>

    <!-- Dialog: Eliminar -->
    <v-dialog v-model="mostrarDialogoEliminar" max-width="500px">
      <dialogo-eliminar
        :nombre="itemSeleccionado"
        @cancelar="cerrarDialogoEliminar"
        @eliminar="confirmarEliminar"
      />
    </v-dialog>

    <!-- Credencial -->
    <credencial-miembro
      v-if="mostrarCredencial"
      :matricula="matriculaSeleccionada"
      :miembro="miembro"
      @impreso="onImpreso"
    />

    <!-- Dialog: Estado de Cuenta -->
    <v-dialog v-model="modalEstadoCuenta" max-width="620px" scrollable>
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Estado de cuenta</h3>
          <p class="modal-sub">
            {{ miembroActual.nombre }} — {{ miembroActual.matricula }}
          </p>
        </div>

        <div class="modal-body">
          <div class="table-wrapper">
            <table class="dash-table-plain">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Membresía</th>
                  <th>Monto</th>
                  <th>Cobrado por</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pago in datosCuenta" :key="pago.fecha">
                  <td class="table-text-sm">
                    {{ formatearFechaHora(pago.fecha) }}
                  </td>
                  <td>
                    <span class="status-tag status-tag-neutral">{{
                      pago.nombre_membresia
                    }}</span>
                  </td>
                  <td class="table-amount">${{ pago.monto }}</td>
                  <td class="table-text-sm">{{ pago.cobrado_por }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-label">Total pagado</div>
              <div class="summary-value summary-value-accent">
                ${{ totalPagado }}
              </div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Total transacciones</div>
              <div class="summary-value">{{ datosCuenta.length }}</div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="modalEstadoCuenta = false">
            Cerrar
          </button>
          <button class="btn-primary" @click="descargarEstadoCuenta">
            <v-icon size="15" class="mr-2">mdi-download</v-icon>
            Descargar PDF
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Dialog: Quitar Membresía -->
    <v-dialog
      v-model="mostrarDialogoQuitarMembresia"
      max-width="480px"
      persistent
    >
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Quitar membresía</h3>
        </div>

        <div class="modal-body">
          <p class="modal-text">
            ¿Estás seguro de quitar la membresía a
            <strong>{{
              miembroSeleccionado ? miembroSeleccionado.nombre : ""
            }}</strong
            >?
          </p>

          <div class="alert-box mb-lg">
            <v-icon size="18" color="#FFB84D">mdi-information-outline</v-icon>
            <div class="alert-text">
              Esto también anula el pago asociado a esta membresía — dejará de
              contar en tus reportes de ingresos. Úsalo para corregir errores
              (membresía adquirida por equivocación), no para dar de baja a un
              miembro que sí pagó y solo quiere cancelar.
            </div>
          </div>

          <div class="field">
            <label class="field-label">Motivo *</label>
            <textarea
              v-model="motivoQuitarMembresia"
              class="field-textarea"
              rows="3"
              placeholder="Ej. Se registró por error en la cuenta equivocada..."
            ></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn-ghost"
            @click="mostrarDialogoQuitarMembresia = false"
          >
            Cancelar
          </button>
          <button
            class="btn-warning-solid"
            @click="quitarMembresia"
            :disabled="cargando || !motivoQuitarMembresia"
          >
            <span v-if="!cargando">Confirmar</span>
            <span v-else class="loading-inline"
              ><span class="spinner-sm"></span>Procesando</span
            >
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="mostrarMensaje"
      :timeout="3000"
      :color="mensaje.color"
      top
      right
    >
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <span>{{ mensaje.texto }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import HttpService from "../../Servicios/HttpService";
import Utiles from "../../Servicios/Utiles";
import RealizarPago from "./RealizarPago.vue";
import DialogoEliminar from "../Dialogos/DialogoEliminar.vue";
import CredencialMiembro from "./CredencialMiembro.vue";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  name: "Miembros",
  components: { RealizarPago, DialogoEliminar, CredencialMiembro },

  data: () => ({
    busqueda: "",
    miembrosOriginales: [],
    miembros: [],
    cargando: false,
    paginaActual: 1,
    porPagina: 24,
    mostrarMensaje: false,
    mensaje: {
      color: "",
      texto: ""
    },
    matriculaSeleccionada: "",
    mostrarRealizarPago: false,
    itemSeleccionado: null,
    miembro: null,
    mostrarDialogoEliminar: false,
    mostrarCredencial: false,
    datosCuenta: [],
    miembroActual: {},
    modalEstadoCuenta: false,
    mostrarDialogoQuitarMembresia: false,
    miembroSeleccionado: null,
    motivoQuitarMembresia: "",
    expandedMember: null,
    currentFilter: "all",
    miembroSeleccionadoPago: null,

    stats: [
      { title: "Total Miembros", value: "0", icon: "mdi-account-group" },
      { title: "Activos", value: "0", icon: "mdi-check-circle" },
      { title: "Vencidos", value: "0", icon: "mdi-alert-circle" },
      { title: "Sin Membresía", value: "0", icon: "mdi-account-question" }
    ],

    quickFilters: [
      { label: "Todos", value: "all", icon: "mdi-account-group", active: true },
      {
        label: "Activos",
        value: "ACTIVO",
        icon: "mdi-check-circle",
        active: false
      },
      {
        label: "Vencidos",
        value: "VENCIDO",
        icon: "mdi-alert-circle",
        active: false
      },
      {
        label: "Sin Membresía",
        value: "sin",
        icon: "mdi-account-question",
        active: false
      }
    ]
  }),

  computed: {
    miembrosFiltrados() {
      if (!this.busqueda && this.currentFilter === "all") return this.miembros;

      let filtered = this.miembros;

      if (this.busqueda) {
        const termino = this.busqueda.toLowerCase();
        filtered = filtered.filter(
          miembro =>
            miembro.nombre.toLowerCase().includes(termino) ||
            miembro.matricula.toLowerCase().includes(termino)
        );
      }

      if (this.currentFilter === "sin") {
        filtered = filtered.filter(m => !m.estado);
      } else if (this.currentFilter !== "all") {
        filtered = filtered.filter(m => m.estado === this.currentFilter);
      }

      return filtered;
    },

    // ===== Paginación =====
    totalPaginas() {
      return Math.max(
        1,
        Math.ceil(this.miembrosFiltrados.length / this.porPagina)
      );
    },

    miembrosPaginados() {
      const inicio = (this.paginaActual - 1) * this.porPagina;
      return this.miembrosFiltrados.slice(inicio, inicio + this.porPagina);
    },

    rangoInicio() {
      if (this.miembrosFiltrados.length === 0) return 0;
      return (this.paginaActual - 1) * this.porPagina + 1;
    },

    rangoFin() {
      return Math.min(
        this.paginaActual * this.porPagina,
        this.miembrosFiltrados.length
      );
    },

    // Muestra máximo 5 números de página, centrados en la página actual,
    // para no llenar la barra de números cuando hay muchas páginas.
    paginasVisibles() {
      const total = this.totalPaginas;
      const actual = this.paginaActual;
      const maxVisibles = 5;

      let inicio = Math.max(1, actual - Math.floor(maxVisibles / 2));
      let fin = Math.min(total, inicio + maxVisibles - 1);
      inicio = Math.max(1, fin - maxVisibles + 1);

      const paginas = [];
      for (let i = inicio; i <= fin; i++) paginas.push(i);
      return paginas;
    },

    totalPagado() {
      return this.datosCuenta.reduce(
        (total, pago) => total + parseFloat(pago.monto),
        0
      );
    },

    mensajeIcon() {
      switch (this.mensaje.color) {
        case "success":
          return "mdi-check-circle";
        case "error":
          return "mdi-alert-circle";
        case "warning":
          return "mdi-alert";
        default:
          return "mdi-information";
      }
    }
  },

  watch: {
    // Si el usuario busca, cambia de filtro, o cambia cuántos por página
    // quiere ver, regresamos a la página 1 — evita quedar "varado" en una
    // página 8 que ya no existe tras filtrar.
    busqueda() {
      this.paginaActual = 1;
    },
    currentFilter() {
      this.paginaActual = 1;
    },
    porPagina() {
      this.paginaActual = 1;
    }
  },

  mounted() {
    this.obtenerMiembros();
  },

  methods: {
    puedeVer(permiso) {
      return this.$store.getters.tienePermiso(permiso);
    },
    toggleExpanded(miembro) {
      this.expandedMember =
        this.expandedMember === miembro.id ? null : miembro.id;
    },

    additionalInfo(miembro) {
      return [
        {
          label: "Edad",
          value: miembro.edad || "No registrada",
          icon: "mdi-calendar"
        },
        {
          label: "Dirección",
          value: miembro.direccion || "No registrada",
          icon: "mdi-map-marker"
        },
        {
          label: "Miembro desde",
          value: this.formatearFechaHora(miembro.fechaRegistro),
          icon: "mdi-calendar-start"
        },
        {
          label: "Enfermedades",
          value: miembro.enfermedad || "Ninguna",
          icon: "mdi-hospital"
        },
        {
          label: "Seguridad Social",
          value: miembro.institucion || "No registrada",
          icon: "mdi-hospital-building"
        },
        {
          label: "NSS",
          value: miembro.afiliacion || "No registrado",
          icon: "mdi-card-account-details"
        },
        {
          label: "Inscripción",
          value: miembro.inscripcion_pagada ? "Pagada" : "Pendiente",
          icon: "mdi-cash-check"
        }
      ];
    },

    aplicarFiltro(valor) {
      this.currentFilter = valor;
      this.quickFilters.forEach(f => {
        f.active = f.value === valor;
      });
    },

    reiniciarFiltros() {
      this.busqueda = "";
      this.aplicarFiltro("all");
    },

    exportarMiembros() {
      this.mostrarMensaje = true;
      this.mensaje = {
        color: "info",
        texto: "Funcionalidad de exportación en desarrollo"
      };
    },

    confirmarQuitarMembresia(miembro) {
      this.miembroSeleccionado = miembro;
      this.motivoQuitarMembresia = "";
      this.mostrarDialogoQuitarMembresia = true;
    },

    async quitarMembresia() {
      this.cargando = true;

      const payload = {
        metodo: "quitar_membresia",
        id: this.miembroSeleccionado.id,
        motivo: this.motivoQuitarMembresia
      };

      try {
        const resultado = await HttpService.eliminar("miembros.php", payload);

        if (resultado && resultado.success) {
          this.mostrarMensaje = true;
          this.mensaje = {
            color: "success",
            texto: "✅ " + resultado.message
          };

          const index = this.miembros.findIndex(
            m => m.id === this.miembroSeleccionado.id
          );
          if (index !== -1) {
            this.$set(this.miembros, index, {
              ...this.miembros[index],
              estado: null,
              fechaInicio: null,
              fechaFin: null,
              membresia: null,
              idMembresia: null
            });
          }

          this.actualizarStats();
        } else {
          this.mostrarMensaje = true;
          this.mensaje = {
            color: "error",
            texto:
              "❌ " +
              ((resultado && resultado.message) ||
                "No se pudo quitar la membresía")
          };
        }
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          color: "error",
          texto: "❌ Error al quitar la membresía"
        };
      } finally {
        this.mostrarDialogoQuitarMembresia = false;
        this.cargando = false;
      }
    },

    formatearFechaHora(fechaString) {
      if (!fechaString) return "Sin fecha";

      try {
        const fecha = new Date(fechaString);
        if (isNaN(fecha.getTime())) return "Fecha inválida";

        const options = {
          day: "2-digit",
          month: "short",
          year: "numeric"
        };
        return fecha.toLocaleDateString("es-ES", options);
      } catch (e) {
        return "Fecha inválida";
      }
    },

    async descargarEstadoCuenta() {
      try {
        const doc = new jsPDF();

        doc.setFontSize(20);
        doc.text("Estado de Cuenta", 14, 20);

        doc.setFontSize(12);
        doc.text(`Miembro: ${this.miembroActual.nombre}`, 14, 30);
        doc.text(`Matrícula: ${this.miembroActual.matricula}`, 14, 38);
        doc.text(`Fecha: ${new Date().toLocaleDateString()}`, 14, 46);

        autoTable(doc, {
          startY: 55,
          head: [["Fecha", "Membresía", "Monto", "Cobrado por"]],
          body: this.datosCuenta.map(p => [
            this.formatearFechaHora(p.fecha),
            p.nombre_membresia,
            `$${parseFloat(p.monto).toFixed(2)}`,
            p.cobrado_por
          ]),
          theme: "striped"
        });

        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(12);
        doc.setFont(undefined, "bold");
        doc.text(`Total Pagado: $${this.totalPagado.toFixed(2)}`, 14, finalY);

        doc.save(
          `Estado_Cuenta_${this.miembroActual.nombre.replace(/\s+/g, "_")}.pdf`
        );

        this.mostrarMensaje = true;
        this.mensaje = {
          color: "success",
          texto: "PDF generado correctamente"
        };
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          color: "error",
          texto: "❌ Error al generar el PDF"
        };
      }
    },

    verEstadoCuenta(miembro) {
      const payload = {
        metodo: "estado_cuenta_miembro",
        matricula: miembro.matricula
      };

      this.cargando = true;
      HttpService.obtenerConDatos(payload, "miembros.php")
        .then(resultado => {
          this.datosCuenta = resultado;
          this.miembroActual = miembro;
          this.modalEstadoCuenta = true;
          this.cargando = false;
        })
        .catch(error => {
          this.cargando = false;
          this.mostrarMensaje = true;
          this.mensaje = {
            color: "error",
            texto: "❌ Error al cargar el estado de cuenta"
          };
        });
    },

    generarCredencial(miembro) {
      this.matriculaSeleccionada = miembro.matricula;
      this.miembro = miembro;
      this.mostrarCredencial = true;
    },

    onImpreso(resultado) {
      this.mostrarCredencial = resultado;
      if (!resultado) {
        this.mostrarMensaje = true;
        this.mensaje = {
          color: "success",
          texto: "✅ Credencial generada correctamente"
        };
      }
    },

    eliminar(miembro) {
      this.itemSeleccionado = miembro.nombre;
      this.miembro = miembro;
      this.mostrarDialogoEliminar = true;
    },

    cerrarDialogoEliminar() {
      this.mostrarDialogoEliminar = false;
      this.miembro = null;
    },

    async confirmarEliminar() {
      this.cargando = true;
      let payload = {
        metodo: "eliminar",
        id: this.miembro.id
      };

      try {
        const eliminado = await HttpService.eliminar("miembros.php", payload);
        if (eliminado) {
          this.mostrarDialogoEliminar = false;
          this.mostrarMensaje = true;
          this.mensaje = {
            color: "success",
            texto: "✅ Miembro eliminado correctamente"
          };
          await this.obtenerMiembros();
        }
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          color: "error",
          texto: "❌ Error al eliminar el miembro"
        };
      } finally {
        this.cargando = false;
      }
    },

    editar(idMiembro) {
      this.$router.push({
        name: "EditarMiembro",
        params: { id: idMiembro }
      });
    },

    urlImagen(imagen) {
      return Utiles.generarURL(imagen);
    },

    cerrarDialogoPago(resultado) {
      this.mostrarRealizarPago = resultado;
      this.miembroSeleccionadoPago = null;
    },

    realizarPago(miembro) {
      this.matriculaSeleccionada = miembro.matricula;
      this.miembroSeleccionadoPago = miembro;
      this.mostrarRealizarPago = true;
    },

    onPagado(resultado) {
      if (resultado) {
        this.mostrarRealizarPago = false;
        this.mostrarMensaje = true;
        this.mensaje = {
          color: "success",
          texto: " Pago realizado con éxito"
        };
        this.obtenerMiembros();
      }
    },

    async obtenerMiembros() {
      this.cargando = true;
      const payload = { metodo: "get" };

      try {
        const resultado = await HttpService.obtenerConDatos(
          payload,
          "miembros.php"
        );
        this.miembros = resultado;
        this.miembrosOriginales = [...resultado];
        this.actualizarStats();
      } catch (error) {
        this.mostrarMensaje = true;
        this.mensaje = {
          color: "error",
          texto: "❌ Error al cargar los miembros"
        };
      } finally {
        this.cargando = false;
      }
    },

    actualizarStats() {
      const total = this.miembros.length;
      const activos = this.miembros.filter(m => m.estado === "ACTIVO").length;
      const vencidos = this.miembros.filter(m => m.estado === "VENCIDO").length;
      const sinMembresia = this.miembros.filter(m => !m.estado).length;

      this.stats[0].value = total;
      this.stats[1].value = activos;
      this.stats[2].value = vencidos;
      this.stats[3].value = sinMembresia;
    },

    estadoHeaderClass(val) {
      return val === "ACTIVO"
        ? "card-top-active"
        : val === "VENCIDO"
        ? "card-top-expired"
        : "card-top-inactive";
    },

    estadoBadgeClass(val) {
      return val === "ACTIVO"
        ? "status-tag-success"
        : val === "VENCIDO"
        ? "status-tag-error"
        : "status-tag-neutral";
    },

    estadoIcon(val) {
      switch (val) {
        case "ACTIVO":
          return "mdi-check-circle";
        case "VENCIDO":
          return "mdi-alert-circle";
        default:
          return "mdi-account-question";
      }
    },

    claseMembresia(nombre) {
      if (!nombre) return "chip-neutral";
      const tipo = nombre.toLowerCase();
      if (tipo.includes("semanal") || tipo.includes("semestral"))
        return "chip-accent";
      return "chip-neutral";
    },

    claseMembresiaBadge(nombre) {
      return "status-tag-neutral";
    },

    iconoMembresia(nombre) {
      if (!nombre) return "mdi-certificate";
      const tipo = nombre.toLowerCase();
      if (tipo.includes("semanal")) return "mdi-trophy";
      if (tipo.includes("mensual hombre")) return "mdi-face-man";
      if (tipo.includes("mensual mujer")) return "mdi-face-woman";
      if (tipo.includes("mensual pareja") || tipo.includes("estudiante"))
        return "mdi-human-male-female";
      if (tipo.includes("semestral")) return "mdi-calendar-range";
      return "mdi-certificate";
    },

    filtrarMiembros() {
      // Handled in computed
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap");

/* ===== TOKENS ===== */
.members-page {
  --bg: #0e0e10;
  --card: #17171a;
  --card-hover: #1b1b1f;
  --card-border: #26262a;
  --text-primary: #f2f2ed;
  --text-secondary: #8c8c93;
  --text-tertiary: #5c5c62;
  --accent: #c8ff4d;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #ffb84d;
  --error: #ff6b6b;
  --radius: 4px;

  --font-display: "Space Grotesk", sans-serif;
  --font-body: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: "JetBrains Mono", monospace;
}

.members-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image: linear-gradient(
      rgba(242, 242, 237, 0.02) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.members-shell {
  position: relative;
  z-index: 1;
  max-width: 1280px;
  margin: 0 auto;
}

.dash-section {
  margin-bottom: 28px;
}

.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 22px 24px;
}

.section-divider {
  height: 1px;
  background: var(--card-border);
  margin: 18px 0;
}

/* ===== STATS ===== */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
}

.stat-card {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 18px;
  animation: fadeInUp 0.4s ease both;
}

.stat-top {
  margin-bottom: 14px;
}

.stat-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-value {
  font-family: var(--font-display);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12.5px;
  color: var(--text-secondary);
}

/* ===== FILTROS ===== */
.filters-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.search-section {
  flex: 1;
  min-width: 260px;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 0 12px;
  transition: border-color 0.15s ease;
}

.search-input-wrapper:focus-within {
  border-color: var(--accent);
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 11px 0;
  color: var(--text-primary);
  font-size: 13.5px;
  font-family: var(--font-body);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.clear-btn {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
}

.action-buttons-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.quick-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 7px 13px;
  background: transparent;
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  color: var(--text-secondary);
  font-size: 12.5px;
  font-family: var(--font-body);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.filter-chip:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.filter-chip-active {
  border-color: var(--accent);
  color: var(--accent);
  background: var(--accent-dim);
}

/* ===== LOADING / EMPTY ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 0;
  gap: 16px;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 56px 20px;
}

.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
}

.empty-desc {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

/* ===== MEMBERS GRID ===== */
.members-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}

.member-card {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-top: 2px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
  animation: fadeInUp 0.4s ease both;
}

.member-card.card-top-active {
  border-top-color: var(--accent);
}

.member-card.card-top-expired {
  border-top-color: var(--error);
}

.member-card.card-top-inactive {
  border-top-color: var(--card-border);
}

.member-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 0;
}

.member-avatar-wrapper {
  width: 48px;
  height: 48px;
}

.member-avatar {
  width: 100%;
  height: 100%;
  border-radius: var(--radius);
  object-fit: cover;
  border: 1px solid var(--card-border);
}

.member-card-content {
  padding: 14px 16px 16px;
}

.member-name {
  font-family: var(--font-display);
  font-size: 15.5px;
  font-weight: 700;
  margin: 0 0 12px;
}

.member-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.detail-item {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.detail-label {
  color: var(--text-tertiary);
  margin-right: 4px;
}

.detail-value {
  color: var(--text-primary);
  font-weight: 600;
}

.detail-value-plain {
  color: var(--text-secondary);
}

.membership-block {
  padding-top: 12px;
  border-top: 1px solid var(--card-border);
}

.membership-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: var(--radius);
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 8px;
}

.membership-chip.chip-accent {
  background: var(--accent-dim);
  color: var(--accent);
}

.membership-chip.chip-neutral {
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-secondary);
}

.membership-dates {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-mini {
  display: flex;
  align-items: center;
  font-size: 11.5px;
  color: var(--text-tertiary);
}

/* Acciones */
.member-card-actions {
  display: flex;
  justify-content: space-around;
  padding: 10px 12px;
  border-top: 1px solid var(--card-border);
  flex-wrap: wrap;
  gap: 4px;
}

.icon-btn {
  width: 30px;
  height: 30px;
  border-radius: var(--radius);
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn:hover {
  border-color: var(--card-border);
  background: rgba(255, 255, 255, 0.03);
}

.icon-btn-danger:hover {
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.06);
}

/* Expandido */
.member-expanded {
  padding: 0 16px 16px;
}

.expanded-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.expanded-item {
  display: flex;
  align-items: flex-start;
}

.expanded-label {
  font-size: 10.5px;
  color: var(--text-tertiary);
}

.expanded-value {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

.alert-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(255, 184, 77, 0.08);
  border: 1px solid rgba(255, 184, 77, 0.25);
  border-radius: var(--radius);
  padding: 12px 14px;
}

.alert-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

.alert-text {
  font-size: 11.5px;
  color: var(--text-secondary);
}

/* ===== STATUS TAG ===== */
.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: var(--radius);
  font-size: 10px;
  font-weight: 600;
  font-family: var(--font-mono);
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.status-tag-success {
  background: var(--accent-dim);
  color: var(--accent);
}

.status-tag-error {
  background: rgba(255, 107, 107, 0.12);
  color: var(--error);
}

.status-tag-neutral {
  background: rgba(140, 140, 147, 0.14);
  color: var(--text-secondary);
}

/* ===== FAB ===== */
.fab-button {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--accent);
  border: none;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.fab-button:hover {
  transform: scale(1.06);
  filter: brightness(1.05);
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: default;
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover:not(:disabled) {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.btn-ghost:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ===== PAGINACIÓN ===== */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--card-border);
}

.pagination-info {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-tertiary);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  background: transparent;
  color: var(--text-secondary);
  font-size: 12.5px;
  font-family: var(--font-mono);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.page-btn:hover:not(:disabled) {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.page-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.page-btn-active {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--bg);
  font-weight: 700;
}

.per-page-select {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 7px 10px;
  color: var(--text-secondary);
  font-family: var(--font-body);
  font-size: 12px;
  outline: none;
  cursor: pointer;
}

.per-page-select:focus {
  border-color: var(--accent);
}

.btn-warning-solid {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--warning);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-warning-solid:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-warning-solid:disabled {
  opacity: 0.6;
  cursor: default;
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.spinner-sm {
  width: 11px;
  height: 11px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== MODAL ===== */
.modal-panel {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.modal-head {
  padding: 22px 24px;
  border-bottom: 1px solid var(--card-border);
}

.modal-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 4px;
}

.modal-sub {
  font-size: 12.5px;
  color: var(--text-secondary);
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.modal-text {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 18px;
}

.modal-text strong {
  color: var(--text-primary);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
}

.table-wrapper {
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
  margin-bottom: 20px;
}

.dash-table-plain {
  width: 100%;
  border-collapse: collapse;
}

.dash-table-plain th {
  text-align: left;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  background: rgba(255, 255, 255, 0.02);
  padding: 10px 12px;
  border-bottom: 1px solid var(--card-border);
}

.dash-table-plain td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--card-border);
  font-size: 12.5px;
}

.dash-table-plain tr:last-child td {
  border-bottom: none;
}

.table-text-sm {
  color: var(--text-secondary);
}

.table-amount {
  color: var(--accent);
  font-weight: 600;
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.summary-item {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 16px;
  text-align: center;
}

.summary-label {
  font-size: 11.5px;
  color: var(--text-tertiary);
  margin-bottom: 6px;
}

.summary-value {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
}

.summary-value-accent {
  color: var(--accent);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
}

.field-textarea {
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 10px 12px;
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: 13px;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s ease;
}

.field-textarea:focus {
  border-color: var(--accent);
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 {
  animation-delay: 0.05s;
}
.delay-200 {
  animation-delay: 0.1s;
}
.delay-300 {
  animation-delay: 0.15s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in,
  .stat-card,
  .member-card,
  .spinner,
  .spinner-sm {
    animation: none;
  }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .members-page {
    padding: 20px;
  }

  .filters-row {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons-row {
    width: 100%;
  }

  .action-buttons-row .btn-primary,
  .action-buttons-row .btn-ghost {
    flex: 1;
  }

  .members-grid {
    grid-template-columns: 1fr;
  }

  .expanded-grid {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Miembros/Miembros.vue